import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Divider,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography,
} from 'components/mui';
import {
	DataGrid,
	GridColDef,
	GridRenderCellParams,
	GridValueGetterParams,
} from '@mui/x-data-grid';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { useIsMobile } from 'hooks/useIsMobile';
import { GetListRoster, GetListRosterAsOf } from 'api/canmail/CANmailAPI';
import {
	getLotusBlossomForwardedHost,
	PurchasesByEmail,
} from 'api/lotusBlossom/lotusBlossomAPI';
import {
	buildParticipants,
	countParticipants,
	filterParticipants,
	withPurchaseDates,
	Participant,
	ParticipantType,
	RosterContact,
} from '../libs/participants';
import { sessionPurchase } from '../libs/purchases';
import { ThemeSession } from '../libs/themeLists';

interface ParticipantsGridProps {
	session: ThemeSession;
	subscribersListId: number | null;
	/**
	 * "YYYY-MM-DD" to rebuild both lists as they stood at the end of that day,
	 * for a session that has already happened. Omit for the current roster.
	 */
	asOfDate?: string;
	/** From the page's single Stripe read; null while it is still loading. */
	purchases: PurchasesByEmail | null;
}

type LoadState = 'loading' | 'error' | 'ready';

const EMPTY_CELL = '—';

// The free DataGrid caps a page at 100 rows, so pagination can't be turned off.
// Under the cap the footer is hidden and every row renders at once; over it the
// footer returns rather than silently dropping rows.
const MAX_ROWS_PER_PAGE = 100;

// Roomier than the grid's 52/56 defaults: rows carry a chip and a date, both of
// which look cramped at the default height.
const ROW_HEIGHT = 60;
const HEADER_HEIGHT = 52;

function TypeChip({ type }: { type: ParticipantType }) {
	// Someone on both lists is shown as a member, since that is what determines
	// their access.
	return type === 'member' ? (
		<Chip
			label="Member"
			size="small"
			color="primary"
			title="Subscriber — included in every session"
		/>
	) : (
		<Chip
			label="One-time"
			size="small"
			variant="outlined"
			title="Booked this session only — not a monthly member"
		/>
	);
}

// Every column flexes, so on a wide screen the spare width is shared out rather
// than pooling into one gap that strands the last three columns at the right
// edge. The ratios are roughly the width each column's content needs.
const buildColumns = (purchasesLoading: boolean): GridColDef[] => [
	{ field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
	{ field: 'email', headerName: 'Email', flex: 1.4, minWidth: 220 },
	{
		field: 'phone',
		headerName: 'Phone',
		flex: 0.9,
		minWidth: 140,
		// v5 orders these generics <Value, Row, Formatted>, so the row type is second.
		renderCell: (params: GridRenderCellParams<unknown, Participant>) => (
			<Typography variant="body2" color={params.row.phone ? 'text.primary' : 'text.disabled'}>
				{params.row.phone || EMPTY_CELL}
			</Typography>
		),
	},
	{
		field: 'purchased',
		// Members show when their membership started, one-time bookings when they
		// paid, so the header has to cover both.
		headerName: 'Purchased',
		// The floor fits "Member since Mar 4, 2025" without wrapping.
		flex: 1.2,
		minWidth: 200,
		sortable: true,
		// Sort by the date itself: the label would order "Apr" before "Dec" and
		// group every "Member since" row under M. ISO strings sort by time, and
		// the unknown '' sorts first.
		valueGetter: (params: GridValueGetterParams<unknown, Participant>) =>
			params.row.purchasedAt,
		renderCell: (params: GridRenderCellParams<unknown, Participant>) =>
			purchasesLoading ? (
				<Typography variant="body2" color="text.disabled">
					Loading…
				</Typography>
			) : (
				<Typography
					variant="body2"
					color={
						params.row.type === 'member' || !params.row.purchased
							? 'text.secondary'
							: 'text.primary'
					}
					title={
						params.row.type === 'member' ? 'Covered by their membership' : undefined
					}
				>
					{params.row.purchased || EMPTY_CELL}
				</Typography>
			),
	},
	{
		field: 'type',
		headerName: 'Type',
		flex: 0.6,
		minWidth: 120,
		sortable: true,
		renderCell: (params: GridRenderCellParams<unknown, Participant>) => (
			<TypeChip type={params.row.type} />
		),
	},
];

function ParticipantCard({ participant }: { participant: Participant }) {
	return (
		<Paper
			elevation={0}
			sx={{
				p: 2,
				border: 1,
				borderColor: 'divider',
				borderRadius: 2,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					gap: 1,
					mb: 1,
				}}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
					{participant.name}
				</Typography>
				<TypeChip type={participant.type} />
			</Box>

			<Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
				{participant.email || EMPTY_CELL}
			</Typography>
			<Typography
				variant="body2"
				color={participant.phone ? 'text.secondary' : 'text.disabled'}
			>
				{participant.phone || EMPTY_CELL}
			</Typography>

			{participant.purchased && (
				<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
					{/* Members already carry their own "Member since …" wording. */}
					{participant.type === 'member'
						? participant.purchased
						: `Purchased ${participant.purchased}`}
				</Typography>
			)}
		</Paper>
	);
}

/**
 * The consolidated roster for one session: one-time buyers from the session's
 * own theme list, plus the members from the subscribers list. Pass `asOfDate`
 * for a session that has already happened.
 */
function ParticipantsGrid({
	session,
	subscribersListId,
	asOfDate,
	purchases,
}: ParticipantsGridProps) {
	const { notifyError } = useNotificationProvider();
	const isMobile = useIsMobile();
	const [state, setState] = useState<LoadState>('loading');
	const [roster, setRoster] = useState<Participant[]>([]);
	const [errorDetail, setErrorDetail] = useState('');
	const [search, setSearch] = useState('');
	const [typeFilter, setTypeFilter] = useState<ParticipantType | 'all'>('all');
	// Bumped per load and on unmount, so a response that has been overtaken —
	// or arrives after the admin has left the tab — is dropped, error toast
	// included.
	const requestRef = useRef(0);

	const load = useCallback(async () => {
		const request = ++requestRef.current;
		const isCurrent = () => request === requestRef.current;

		setState('loading');
		try {
			// The subscribers list may not exist yet — created lazily by the first
			// subscription — so treat null as "no members" rather than an error.
			// These ids come from the legacy /api2.0 backend, so the roster calls
			// are told the same tenant to resolve them against.
			const tenantHost = getLotusBlossomForwardedHost();

			// A past session's attendance is whoever was on its theme list that
			// day — an unsubscribe or bounce cleanup since has removed people from
			// the list itself — plus whoever is on it now, since the rebuild reads
			// only the consent log and a contact added without an entry there
			// would otherwise vanish. buildParticipants merges the overlap.
			const loadThemeContacts = async (): Promise<RosterContact[]> => {
				if (!asOfDate) {
					return (await GetListRoster(session.listId, tenantHost))?.contacts ?? [];
				}
				const [onTheDay, now] = await Promise.all([
					GetListRosterAsOf(session.listId, asOfDate, tenantHost),
					GetListRoster(session.listId, tenantHost),
				]);
				return [...(onTheDay?.contacts ?? []), ...(now?.contacts ?? [])];
			};

			// Membership, by contrast, must be as of the day: the subscribers list
			// holds only who is subscribed right now.
			const loadSubscriberContacts = async (): Promise<RosterContact[]> => {
				if (!subscribersListId) return [];
				const roster = asOfDate
					? await GetListRosterAsOf(subscribersListId, asOfDate, tenantHost)
					: await GetListRoster(subscribersListId, tenantHost);
				return roster?.contacts ?? [];
			};

			const [themeContacts, subscriberContacts] = await Promise.all([
				loadThemeContacts(),
				loadSubscriberContacts(),
			]);
			if (!isCurrent()) return;

			setRoster(buildParticipants(themeContacts, subscriberContacts));
			setState('ready');
		} catch (error) {
			if (!isCurrent()) return;
			console.error(
				`[QuantumHealing] ParticipantsGrid load failed (list ${session.listId}, asOf ${asOfDate ?? 'now'}):`,
				error
			);
			notifyError('Could not load the participant list.');
			setErrorDetail(error instanceof Error ? error.message : String(error));
			setState('error');
		}
	}, [session, subscribersListId, asOfDate, notifyError]);

	useEffect(() => {
		load();
		return () => {
			requestRef.current += 1;
		};
	}, [load]);

	// Purchase dates join the roster whenever Stripe answers, without holding
	// it up.
	const participants = useMemo(
		() =>
			purchases
				? withPurchaseDates(roster, (p) =>
						sessionPurchase(purchases, p.email, session, p.type === 'member')
				  )
				: roster,
		[roster, purchases, session]
	);
	const columns = useMemo(() => buildColumns(purchases === null), [purchases]);

	const counts = useMemo(() => countParticipants(participants), [participants]);
	const visible = useMemo(
		() => filterParticipants(participants, search, typeFilter),
		[participants, search, typeFilter]
	);

	const emptyMessage =
		participants.length === 0
			? 'Nobody has enrolled in this session yet.'
			: 'No participants match this filter.';

	if (state === 'error') {
		return (
			<Alert severity="error">
				We couldn&apos;t load the participant list. Please refresh the page — if it
				keeps happening, contact support.
				{errorDetail && (
					<Typography variant="caption" component="div" sx={{ mt: 1 }}>
						{errorDetail}
					</Typography>
				)}
			</Alert>
		);
	}

	return (
		// Controls, counts and roster deliberately share one Paper — the client
		// wanted a single card, not the two the UI standard would give.
		<Paper elevation={1}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					gap: 2,
					alignItems: { sm: 'center' },
					justifyContent: 'space-between',
					p: { xs: 2, sm: 2.5 },
				}}
			>
				{/* Uncomment once rosters outgrow scanning by eye. */}
				{/* <TextField
					label="Search participants"
					size="small"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ flex: 1, minWidth: 0 }}
				/> */}
				<FormControl size="small" sx={{ minWidth: 180 }}>
					<InputLabel id="participant-type-label">Type</InputLabel>
					<Select
						labelId="participant-type-label"
						label="Type"
						value={typeFilter}
						onChange={(e) => setTypeFilter(e.target.value as ParticipantType | 'all')}
					>
						<MenuItem value="all">All</MenuItem>
						<MenuItem value="member">Members</MenuItem>
						<MenuItem value="one-time">One-time</MenuItem>
					</Select>
				</FormControl>

				<Typography variant="body2" color="text.secondary">
					{counts.total} participant{counts.total === 1 ? '' : 's'} ·{' '}
					{counts.members} member{counts.members === 1 ? '' : 's'} ·{' '}
					{counts.oneTime} one-time
				</Typography>
			</Box>

			<Divider />

			{isMobile ? (
				<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
					{state === 'loading' && (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress size={28} />
						</Box>
					)}

					{state === 'ready' && visible.length === 0 && (
						<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
							{emptyMessage}
						</Typography>
					)}

					{visible.map((participant) => (
						<ParticipantCard key={participant.id} participant={participant} />
					))}
				</Box>
			) : (
				<DataGrid
					rows={visible}
					columns={columns}
					loading={state === 'loading'}
					autoHeight
					disableSelectionOnClick
					initialState={{
						sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
					}}
					pageSize={MAX_ROWS_PER_PAGE}
					rowsPerPageOptions={[MAX_ROWS_PER_PAGE]}
					hideFooter={visible.length <= MAX_ROWS_PER_PAGE}
					localeText={{ noRowsLabel: emptyMessage }}
					rowHeight={ROW_HEIGHT}
					headerHeight={HEADER_HEIGHT}
					sx={{
						border: 'none',
						// The grid's own 10px cell padding is tighter than the control
						// bar above it, so the columns read as crowded and the first one
						// sits left of the filter. Widen it, and line the outer edges up
						// with the bar.
						'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': { px: 1.5 },
						'& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type':
							{ pl: { xs: 2, sm: 2.5 } },
						'& .MuiDataGrid-columnHeader:last-of-type, & .MuiDataGrid-cell:last-of-type':
							{ pr: { xs: 2, sm: 2.5 } },
					}}
				/>
			)}
		</Paper>
	);
}

export default ParticipantsGrid;
